import { ApiService } from "../../../config";

export type InstagramConnectionStatus =
  | "ACTIVE"
  | "TOKEN_EXPIRED"
  | "PERMISSION_DENIED"
  | "REVOKED"
  | "DISCONNECTED";

export type InstagramMediaStatus =
  | "PENDING_REVIEW"
  | "APPROVED"
  | "PUBLISHING"
  | "PUBLISHED"
  | "SKIPPED"
  | "REJECTED"
  | "FAILED";

export type InstagramConnection = {
  id: number;
  username: string | null;
  accountType: string | null;
  status: InstagramConnectionStatus;
  syncEnabled: boolean;
  autoPublish: boolean;
  maxPostsPerDay: number;
  activatedAt: string | null;
  consentedAt: string | null;
  tokenExpiresAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
};

export type InstagramMediaItem = {
  id: number;
  connectionId: number;
  igMediaId: string;
  permalink: string | null;
  mediaType: string | null;
  caption: string | null;
  mediaTimestamp: string | null;
  status: InstagramMediaStatus;
  skipReason: string | null;
  summary: string | null;
  imageUrl: string | null;
  googleSearchUrl: string | null;
  errorMessage: string | null;
  publishedAt: string | null;
};

export type InstagramPreviewMedia = {
  igMediaId: string;
  permalink: string | null;
  mediaType: string | null;
  caption: string | null;
  thumbnailUrl: string | null;
  timestamp: string | null;
  eligible: boolean;
  ineligibleReason: string | null;
  adaptedSummary: string | null;
};

export type InstagramOverview = {
  integrationEnabled: boolean;
  oauthConfigured: boolean;
  syncEnabled: boolean;
  publishEnabled: boolean;
  consentTextVersion: string;
  consentText: string;
  connections: InstagramConnection[];
  items: InstagramMediaItem[];
};

type Envelope<T> = { message?: string; error?: boolean; data?: T };

// O backend responde 200 com {error:true} para erro de negocio (BusinessException).
const unwrap = <T>(response: { data?: Envelope<T> }): T => {
  const body = response?.data;
  if (!body || body.error) {
    throw new Error(body?.message || "Não foi possível concluir a operação.");
  }
  return body.data as T;
};

export const INSTAGRAM_PENDING_KEY = "georanking.instagram.pendingProfile";
export const INSTAGRAM_RETURN_KEY = "georanking.instagram.returnTo";

export const instagramApi = {
  async overview(profileId: string): Promise<InstagramOverview> {
    return unwrap(await ApiService.get(`/business-profiles/${profileId}/instagram`));
  },

  async start(profileId: string): Promise<string> {
    const r = unwrap<{ authorizationUrl: string }>(
      await ApiService.post(`/business-profiles/${profileId}/instagram/oauth/start`, {}),
    );
    return r.authorizationUrl;
  },

  async complete(code: string, state: string): Promise<InstagramConnection> {
    return unwrap(await ApiService.post(`/integrations/instagram/oauth/complete`, { code, state }));
  },

  async updateSettings(
    profileId: string,
    connectionId: number,
    body: {
      syncEnabled?: boolean;
      maxPostsPerDay?: number;
      consentAccepted?: boolean;
      consentTextVersion?: string;
    },
  ): Promise<InstagramConnection> {
    return unwrap(
      await ApiService.put(`/business-profiles/${profileId}/instagram/${connectionId}/settings`, body),
    );
  },

  async disconnect(profileId: string, connectionId: number): Promise<void> {
    unwrap(await ApiService.delete(`/business-profiles/${profileId}/instagram/${connectionId}`));
  },

  async preview(profileId: string, connectionId: number): Promise<InstagramPreviewMedia[]> {
    return unwrap(
      await ApiService.get(`/business-profiles/${profileId}/instagram/${connectionId}/preview`),
    );
  },

  async editSummary(profileId: string, itemId: number, summary: string): Promise<InstagramMediaItem> {
    return unwrap(
      await ApiService.put(`/business-profiles/${profileId}/instagram/items/${itemId}/summary`, { summary }),
    );
  },

  async approve(profileId: string, itemId: number): Promise<InstagramMediaItem> {
    return unwrap(
      await ApiService.post(`/business-profiles/${profileId}/instagram/items/${itemId}/approve`, {}),
    );
  },

  async reject(profileId: string, itemId: number): Promise<InstagramMediaItem> {
    return unwrap(
      await ApiService.post(`/business-profiles/${profileId}/instagram/items/${itemId}/reject`, {}),
    );
  },
};
